import { useEffect, useRef, useState } from 'react'
import { useAuthStore } from '../../stores/auth'
import colors from '../../colors'
import PlanCards, { PLAN_COPY, PlanPricing } from './PlanCards'
import PaymentStep from './PaymentStep'
import { Box, Button, Typography, CircularProgress, MenuItem } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { TextField } from 'formik-material-ui'
import { Link, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { Formik, Form, Field, useFormikContext } from 'formik'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'

interface SignupValues {
  organization_name: string
  organization_slug: string
  time_zone: string
  email: string
  password: string
  password_confirmation: string
  phone: string
  tier: string
}

const TIER_VALUES = PLAN_COPY.map((plan) => plan.tier)

const proPlan = PLAN_COPY.find((plan) => plan.tier === 'pro')

const INTERVAL_LABELS: Record<string, string> = {
  day: '/day', week: '/wk', month: '/mo', year: '/yr',
}

// sx (emotion) rather than makeStyles: the theme defaults every Button to
// contained/uppercase, and v4 makeStyles classes lose to MUI v5's own styles.
const primaryCtaSx = {
  mt: 3,
  py: 1.25,
  textTransform: 'none',
  fontSize: 15,
  fontWeight: 700,
  borderRadius: '8px',
} as const

const TIME_ZONES = [
  'Eastern Time (US & Canada)',
  'Central Time (US & Canada)',
  'Mountain Time (US & Canada)',
  'Pacific Time (US & Canada)',
  'Arizona',
  'Alaska',
  'Hawaii',
]

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Auto-fills the slug from the org name until the user edits the slug themselves.
const SlugSync = () => {
  const { values, setFieldValue, touched } = useFormikContext<SignupValues>()
  useEffect(() => {
    if (!touched.organization_slug) {
      setFieldValue('organization_slug', slugify(values.organization_name))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.organization_name])
  return null
}

const SignupForm = () => {
  const styles = useStyles()
  const { login } = useAuthStore()
  const navigate = useNavigate()
  const pageLoadedAt = useRef(Date.now())
  const [step, setStep] = useState(1)
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [serviceFees, setServiceFees] = useState<Record<string, number>>({})
  const [prices, setPrices] = useState<Record<string, PlanPricing>>({})

  // Show the fee a new org will actually be charged, rather than hardcoded
  // copy that drifts when the rate is changed in Platform Fees.
  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get<
          { tier: string, serviceFee: number, amount?: number, interval?: string, trialPeriodDays?: number | null }[]
        >('/signup_plans')
        const fees: Record<string, number> = {}
        const livePrices: Record<string, PlanPricing> = {}
        response.data.forEach((plan) => {
          fees[plan.tier] = plan.serviceFee
          // Absent when Stripe is unreachable or no price is configured; the
          // cards then fall back to their own copy.
          if (plan.amount != null) {
            livePrices[plan.tier] = { amount: plan.amount, interval: plan.interval, trialPeriodDays: plan.trialPeriodDays }
          }
        })
        setServiceFees(fees)
        setPrices(livePrices)
      } catch {
        // Non-blocking: the cards still render, just without the fee line.
      }
    })()
  }, [])

  // Mirrors the plan card: live Stripe price when available, copy otherwise.
  const proPriceLabel = prices.pro
    ? `$${Math.round(prices.pro.amount / 100).toLocaleString('en-US')}${INTERVAL_LABELS[prices.pro.interval ?? 'month'] ?? ''}`
    : `$${proPlan?.monthlyPrice ?? 0}/mo`

  const validationSchema = Yup.object({
    organization_name: Yup.string().required('Required'),
    organization_slug: Yup.string()
      .required('Required')
      .matches(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only'),
    time_zone: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    password: Yup.string().min(8, 'At least 8 characters').required('Required'),
    password_confirmation: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Required'),
    phone: Yup.string(),
    tier: Yup.string().oneOf(TIER_VALUES).required('Required'),
  })

  const createOrganization = async (values: SignupValues, paymentMethodId?: string) => {
    try {
      await axios.post('/self_service_signups', {
        ...values,
        ...(paymentMethodId ? { payment_method_id: paymentMethodId } : {}),
        // How long the user spent signing up, so we can measure the
        // "under two minutes" goal. Log-only server side.
        client_elapsed_ms: Date.now() - pageLoadedAt.current,
      })
      const [success, message] = await login(values.email, values.password)
      if (success) {
        navigate('/')
      } else {
        toast.warning(message || 'Account created — please sign in.')
        navigate('/login')
      }
    } catch (e: any) {
      const errors = e?.response?.data?.errors
      if (Array.isArray(errors) && errors.length) {
        toast.error(errors.join(', '))
      } else {
        toast.error('Something went wrong creating your organization. Please try again.')
      }
    }
  }

  // Both steps submit the form, so Enter and the button behave identically.
  // Step one only advances - validation has already passed by the time we
  // get here, so the details are known good before the plan step.
  const onSubmit = async (values: SignupValues, { setSubmitting }: { setSubmitting: (v: boolean) => void }) => {
    if (step === 1) {
      setStep(2)
      setSubmitting(false)
      return
    }

    // Pro needs a card before the org exists - a declined card must create
    // nothing at all, so collect payment first and submit it with the signup.
    if (step === 2 && values.tier === 'pro') {
      setStep(3)
      setSubmitting(false)
      return
    }

    await createOrganization(values)
  }

  return (
    <Formik<SignupValues>
      initialValues={{
        organization_name: '',
        organization_slug: '',
        time_zone: 'Pacific Time (US & Canada)',
        email: '',
        password: '',
        password_confirmation: '',
        phone: '',
        tier: 'core',
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      // Hold errors back until the first Continue. After that, validate live
      // so messages clear as the user fixes each field.
      validateOnBlur={attemptedSubmit}
      validateOnChange={attemptedSubmit}
    >
      {(formikProps) => (
        <Box>
          <Box className={styles.header}>
            <Typography variant="h4" className={styles.headline}>
              {step === 1 && 'Create your organization'}
              {step === 2 && 'Choose your plan'}
              {step === 3 && 'Add your payment details'}
            </Typography>
            {step === 2 && (
              <Typography className={styles.subhead}>
                You can change this later as your membership program grows.
              </Typography>
            )}
          </Box>

          <Form>
            <SlugSync />

            {step === 1 ? (
              <Box className={styles.fieldsContainer}>
                <Field
                  autoFocus
                  fullWidth
                  label="Organization name"
                  component={TextField}
                  name="organization_name"
                />
                <Field
                  fullWidth
                  autoCapitalize="none"
                  label="URL slug"
                  helperText="Used in your links, e.g. my.stitch.vip/your-slug"
                  component={TextField}
                  name="organization_slug"
                />
                <Field
                  select
                  fullWidth
                  label="Time zone"
                  component={TextField}
                  name="time_zone"
                >
                  {TIME_ZONES.map((tz) => (
                    <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                  ))}
                </Field>
                <Field
                  fullWidth
                  autoCapitalize="none"
                  type="email"
                  label="Email"
                  component={TextField}
                  name="email"
                />
                <Field
                  fullWidth
                  label="Phone (optional)"
                  component={TextField}
                  name="phone"
                />
                <Field
                  fullWidth
                  autoCapitalize="none"
                  type="password"
                  label="Password"
                  component={TextField}
                  name="password"
                />
                <Field
                  fullWidth
                  autoCapitalize="none"
                  type="password"
                  label="Confirm password"
                  component={TextField}
                  name="password_confirmation"
                />

                <Button
                  type="submit"
                  fullWidth
                  disableElevation
                  onClick={() => setAttemptedSubmit(true)}
                  sx={primaryCtaSx}
                >
                  Continue
                </Button>

                <Link className={styles.link} to={'/login'}>
                  Already have an account? Sign in
                </Link>
              </Box>
            ) : step === 3 ? (
              <PaymentStep
                priceLabel={proPriceLabel}
                submitting={formikProps.isSubmitting}
                onBack={() => setStep(2)}
                onPaymentMethod={async (paymentMethodId) => {
                  formikProps.setSubmitting(true)
                  await createOrganization(formikProps.values, paymentMethodId)
                  formikProps.setSubmitting(false)
                }}
              />
            ) : (
              <Box>
                <PlanCards
                  value={formikProps.values.tier}
                  onChange={(tier) => formikProps.setFieldValue('tier', tier)}
                  serviceFees={serviceFees}
                  prices={prices}
                />

                <Button
                  type="submit"
                  fullWidth
                  disableElevation
                  disabled={formikProps.isSubmitting}
                  sx={primaryCtaSx}
                >
                  {formikProps.isSubmitting
                    ? <CircularProgress size={16} style={{ marginRight: 8 }} />
                    : null}
                  {formikProps.values.tier === 'pro' ? 'Continue to payment' : 'Create organization'}
                </Button>

                <Button
                  type="button"
                  fullWidth
                  variant="text"
                  disabled={formikProps.isSubmitting}
                  onClick={() => setStep(1)}
                  sx={{
                    mt: 1.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: 15,
                    color: colors.GREY_2,
                    backgroundColor: 'transparent',
                    '&:hover': { backgroundColor: colors.GREY_7 },
                  }}
                >
                  Back
                </Button>
              </Box>
            )}
          </Form>
        </Box>
      )}
    </Formik>
  )
}

const useStyles = makeStyles({
  // One wrapper owns the gap below, so both steps space the same whether or
  // not they have a subhead.
  header: {
    marginBottom: 24,
  },
  headline: {
    fontWeight: 'bold',
    textAlign: 'center',
    color: colors.STITCH_BLACK,
  },
  subhead: {
    textAlign: 'center',
    color: colors.GREY_2,
    marginTop: 8,
  },
  link: {
    color: colors.BRIGHT_BLUE,
    marginTop: 16,
    textAlign: 'center',
  },
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
})

export default SignupForm
